import type { Config, EditorField, NumberConfig, PlayLike } from '../@types/editor'

import {
  EDITOR_QUICK_PICK_BATCH_CASCADE,
  EDITOR_QUICK_PICK_COLLAPSE_TIMEOUT,
  EDITOR_QUICK_PICK_GLOW_IN,
  EDITOR_QUICK_PICK_GLOW_OUT,
  EDITOR_QUICK_PICK_GRID_TIMING,
  EDITOR_QUICK_PICK_ROLL_TIMING,
  EDITOR_QUICK_PICK_SETTLE,
  EDITOR_QUICK_PICK_VISIBILITY_TIMEOUT,
} from '../constants/quickPickAnimation'
import {
  buildQuickPickAnimationPlan,
  getQuickPickRollLockTimes,
  getQuickPickScatterLockTimes,
  pickQuickPickRollValues,
  type QuickPickAnimationSlot,
  type QuickPickAnimationTarget,
} from '../utils/quickPickAnimation'

export type EditorQuickPickAnimationKind = 'ball' | 'digit' | 'grid'

export type EditorQuickPickNumberState = 'locked' | 'rolling'

type Animation = {
  delay: Maybe<number>
  duration: number
  fields: NumberConfig[]
  glow: boolean
  kind: EditorQuickPickAnimationKind
  lockedCount: number
  result: EditorField[]
  rolling: Map<string, number[]>
  slots: QuickPickAnimationSlot[]
  timers: Timer[]
}

type PlayWithId = PlayLike & { id: string }

type RegisterOptions<TPlay extends PlayWithId> = {
  config: Config
  delay?: Maybe<number>
  glow?: boolean
  kind: EditorQuickPickAnimationKind
  play: TPlay
  snapshot: EditorField[]
}

type Timer = ReturnType<typeof setTimeout>

const animations = ref(new Map<string, Animation>())
const highlighted = ref(new Set<string>())
const glowTimers = new Map<string, Timer>()
const startTimers = new Map<string, Timer>()
const visibilityTimers = new Map<string, Timer>()
const pendingStarts = new Set<string>()
const visiblePlays = new Set<string>()

let rollInterval: Maybe<Timer> = null
let scatterInterval: Maybe<Timer> = null

const cloneFields = (fields: EditorField[]): EditorField[] =>
  fields.map(({ name, selected }) => ({ name, selected: [...selected] }))

const normalizeName = (name: string) => name.toLowerCase().replace('_', '-')

const isScatter = (kind: EditorQuickPickAnimationKind) => kind === 'grid'

const getLockTimes = (kind: EditorQuickPickAnimationKind, targets: QuickPickAnimationTarget[]) =>
  isScatter(kind)
    ? getQuickPickScatterLockTimes(targets, EDITOR_QUICK_PICK_GRID_TIMING)
    : getQuickPickRollLockTimes(targets.length, EDITOR_QUICK_PICK_ROLL_TIMING)

const getUnlockedValues = ({ lockedCount, slots }: Animation, fieldName: string): number[] =>
  slots
    .slice(lockedCount)
    .filter((slot) => slot.fieldName === fieldName)
    .map(({ value }) => value)

const updateRolling = (animation: Animation) => {
  animation.fields.forEach(({ name, selectableNumbers }) => {
    const unlocked = getUnlockedValues(animation, name)
    if (!unlocked.length) {
      animation.rolling.delete(name)
      return
    }

    const shown = (animation.result.find((field) => field.name === name)?.selected ?? []).filter(
      (value) => !unlocked.includes(value),
    )

    animation.rolling.set(name, pickQuickPickRollValues(selectableNumbers, shown, unlocked.length))
  })
}

const tick = (scatter: boolean) =>
  animations.value.forEach((animation) => {
    if (isScatter(animation.kind) !== scatter) return
    updateRolling(animation)
  })

const syncIntervals = () => {
  const kinds = [...animations.value.values()].map(({ kind }) => kind)
  const hasRoll = kinds.some((kind) => !isScatter(kind))
  const hasScatter = kinds.some(isScatter)

  if (hasRoll && !rollInterval) {
    rollInterval = setInterval(() => tick(false), EDITOR_QUICK_PICK_ROLL_TIMING.tick)
  }
  if (!hasRoll && rollInterval) {
    clearInterval(rollInterval)
    rollInterval = null
  }

  if (hasScatter && !scatterInterval) {
    scatterInterval = setInterval(() => tick(true), EDITOR_QUICK_PICK_GRID_TIMING.tick)
  }
  if (!hasScatter && scatterInterval) {
    clearInterval(scatterInterval)
    scatterInterval = null
  }
}

const clearGlow = (playId: string) => {
  const timer = glowTimers.get(playId)
  if (timer) clearTimeout(timer)
  glowTimers.delete(playId)
  highlighted.value.delete(playId)
}

const startGlow = (playId: string, duration: number) => {
  clearGlow(playId)
  highlighted.value.add(playId)
  glowTimers.set(
    playId,
    setTimeout(
      () => clearGlow(playId),
      EDITOR_QUICK_PICK_GLOW_IN + duration + EDITOR_QUICK_PICK_GLOW_OUT,
    ),
  )
}

const lock = (playId: string, lockedCount: number) => {
  const animation = animations.value.get(playId)
  if (!animation) return

  animation.lockedCount = lockedCount
  updateRolling(animation)
}

const finish = (playId: string) => {
  animations.value.delete(playId)
  syncIntervals()
}

const start = (playId: string) => {
  const animation = animations.value.get(playId)
  if (!animation) return

  if (animation.glow) startGlow(playId, animation.duration)

  animation.slots.forEach(({ lockTime }, index) => {
    animation.timers.push(setTimeout(() => lock(playId, index + 1), lockTime))
  })
  animation.timers.push(
    setTimeout(() => finish(playId), animation.duration + EDITOR_QUICK_PICK_SETTLE),
  )
}

const clearPendingStart = (playId: string) => {
  const timer = visibilityTimers.get(playId)
  if (timer) clearTimeout(timer)
  visibilityTimers.delete(playId)
  pendingStarts.delete(playId)
}

const scheduleStart = (playId: string) => {
  const animation = animations.value.get(playId)
  if (!animation) return

  const delay = animation.delay ?? startTimers.size * EDITOR_QUICK_PICK_BATCH_CASCADE

  startTimers.set(
    playId,
    setTimeout(() => {
      startTimers.delete(playId)
      start(playId)
    }, delay),
  )
}

const awaitVisibility = (playId: string) => {
  if (visiblePlays.has(playId)) return scheduleStart(playId)

  pendingStarts.add(playId)
  visibilityTimers.set(
    playId,
    setTimeout(() => {
      clearPendingStart(playId)
      scheduleStart(playId)
    }, EDITOR_QUICK_PICK_VISIBILITY_TIMEOUT),
  )
}

const register = <TPlay extends PlayWithId>({
  config,
  delay = null,
  glow = false,
  kind,
  play,
  snapshot,
}: RegisterOptions<TPlay>): Maybe<Animation> => {
  const result = cloneFields(play.fields)
  const { duration, slots } = buildQuickPickAnimationPlan(snapshot, result, (targets) =>
    getLockTimes(kind, targets),
  )
  if (!slots.length) return null

  animations.value.set(play.id, {
    delay,
    duration,
    fields: [...config.numbers],
    glow,
    kind,
    lockedCount: 0,
    result,
    rolling: new Map(),
    slots,
    timers: [],
  })

  const animation = animations.value.get(play.id)
  if (!animation) return null

  updateRolling(animation)
  syncIntervals()

  return animation
}

export const useEditorQuickPickAnimation = () => {
  const prefersReducedMotion = useReducedMotion()

  const isAnimating = (playId: string) => animations.value.has(playId)

  const isHighlighted = (playId: string) => highlighted.value.has(playId)

  const getRollingValues = (playId: string, fieldName: string): number[] =>
    animations.value.get(playId)?.rolling.get(fieldName) ?? []

  const cancel = (playId: string) => {
    const startTimer = startTimers.get(playId)
    if (startTimer) clearTimeout(startTimer)
    startTimers.delete(playId)
    clearPendingStart(playId)

    animations.value.get(playId)?.timers.forEach((timer) => clearTimeout(timer))
    animations.value.delete(playId)
    clearGlow(playId)
    syncIntervals()
  }

  const getDisplayPlay = <TPlay extends PlayWithId>(play: TPlay): TPlay => {
    const animation = animations.value.get(play.id)
    if (!animation) return play

    return {
      ...play,
      fields: play.fields.map((field) => {
        const hidden = getUnlockedValues(animation, field.name)
        if (!hidden.length) return field

        return { ...field, selected: field.selected.filter((value) => !hidden.includes(value)) }
      }),
    }
  }

  const getSummaryPlay = <TPlay extends PlayWithId>(play: TPlay): TPlay => {
    const animation = animations.value.get(play.id)
    if (!animation) return play

    return {
      ...play,
      fields: play.fields.map((field) => {
        const unlocked = getUnlockedValues(animation, field.name)
        if (!unlocked.length) return field

        const rolling = [...(animation.rolling.get(field.name) ?? [])]

        return {
          ...field,
          selected: field.selected.map((value) =>
            unlocked.includes(value) ? (rolling.shift() ?? value) : value,
          ),
        }
      }),
    }
  }

  const getNumberState = (
    playId: string,
    value: number | string | undefined,
    name: string,
  ): Maybe<EditorQuickPickNumberState> => {
    const animation = animations.value.get(playId)
    if (!animation || typeof value !== 'number') return null

    const fieldName = animation.fields.find(
      (field) => normalizeName(field.name) === normalizeName(name),
    )?.name
    if (!fieldName) return null

    if (animation.rolling.get(fieldName)?.includes(value)) return 'rolling'

    return animation.slots
      .slice(0, animation.lockedCount)
      .some((slot) => slot.fieldName === fieldName && slot.value === value)
      ? 'locked'
      : null
  }

  const notifyCollapsed = (playId: string) => {
    const startTimer = startTimers.get(playId)
    if (!startTimer) return

    clearTimeout(startTimer)
    startTimers.delete(playId)
    start(playId)
  }

  const setPlayVisible = (playId: string, visible: boolean) => {
    if (!visible) {
      visiblePlays.delete(playId)
      return
    }

    visiblePlays.add(playId)
    if (!pendingStarts.has(playId)) return

    clearPendingStart(playId)
    scheduleStart(playId)
  }

  const reset = () => {
    animations.value.forEach(({ timers }) => timers.forEach((timer) => clearTimeout(timer)))
    animations.value.clear()
    startTimers.forEach((timer) => clearTimeout(timer))
    startTimers.clear()
    visibilityTimers.forEach((timer) => clearTimeout(timer))
    visibilityTimers.clear()
    pendingStarts.clear()
    visiblePlays.clear()
    glowTimers.forEach((timer) => clearTimeout(timer))
    glowTimers.clear()
    highlighted.value.clear()
    syncIntervals()
  }

  const triggerSinglePlay = <TPlay extends PlayWithId>({
    config,
    ignoredFields = [],
    kind,
    play,
    quickPick,
    replaceAll,
  }: {
    config: Config
    ignoredFields?: string[]
    kind: EditorQuickPickAnimationKind
    play: TPlay
    quickPick: () => void
    replaceAll: boolean
  }) => {
    if (isAnimating(play.id)) return
    if (prefersReducedMotion.value) return quickPick()

    const previous = cloneFields(play.fields)
    const snapshot = replaceAll
      ? previous.filter(({ name }) => ignoredFields.includes(name))
      : previous

    quickPick()

    if (prefersReducedMotion.value) return

    if (register({ config, kind, play, snapshot })) start(play.id)
  }

  const triggerBatch = <TPlay extends PlayWithId>({
    config,
    expandedPlayId,
    plays,
    quickPick,
  }: {
    config: Config
    expandedPlayId: Maybe<string>
    plays: () => TPlay[]
    quickPick: () => string[]
  }): string[] => {
    const snapshots = new Map(plays().map((play) => [play.id, cloneFields(play.fields)]))
    const playIds = quickPick()

    ;[...highlighted.value].forEach(clearGlow)
    playIds.forEach(cancel)
    if (prefersReducedMotion.value) return playIds

    if (prefersReducedMotion.value) return playIds

    playIds.forEach((playId) => {
      const play = plays().find(({ id }) => id === playId)
      if (!play) return

      const registered = register({
        config,
        delay: playId === expandedPlayId ? EDITOR_QUICK_PICK_COLLAPSE_TIMEOUT : null,
        glow: true,
        kind: 'ball',
        play,
        snapshot: snapshots.get(playId) ?? [],
      })
      if (!registered) return

      awaitVisibility(playId)
    })

    return playIds
  }

  return {
    cancel,
    getDisplayPlay,
    getNumberState,
    getRollingValues,
    getSummaryPlay,
    isAnimating,
    isHighlighted,
    notifyCollapsed,
    reset,
    setPlayVisible,
    triggerBatch,
    triggerSinglePlay,
  }
}
